import IconAddCircleBold from "@shared/components/icons/AddCircleBold"
import { changedSinceCopied } from "@shared/lib/followed-course"
import type { ParentCardStack, UnifiedCardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { tracked } from "@supergrain/kernel/react"
import moment from "moment"
import UnifiedLessonSimpleReadOnly from "react-app/components/planner/card-stacks/UnifiedLessonSimpleReadOnly"
import { ThreeDotSpinner } from "react-app/components/util/ThreeDotSpinner"
import { Button } from "react-aria-components"
import { useFollowedLesson, type FollowedLesson } from "../use-followed-lesson"

export const FollowedCourseLessonColumn = tracked(function FollowedCourseLessonColumn({
  followedCourse,
  planbook,
  rotationCalendar,
  day,
  isCurrent,
  parentCardStack,
  onCopyFollowedLesson,
}: {
  followedCourse: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  day: string
  isCurrent: boolean
  parentCardStack: ParentCardStack | null
  onCopyFollowedLesson?: (followedLesson: UnifiedCardStack, followedDateString: string) => void
}) {
  const { courseDate, lesson } = useFollowedLesson(followedCourse, planbook, rotationCalendar, day)
  const loadedCardStack = lesson.status === "loaded" ? lesson.lesson : null
  const isCopied =
    loadedCardStack !== null &&
    parentCardStack?.courseId === followedCourse.id &&
    parentCardStack.id === loadedCardStack.id
  const doCopyLesson =
    loadedCardStack !== null && onCopyFollowedLesson !== undefined
      ? () => onCopyFollowedLesson(loadedCardStack, day)
      : undefined

  return (
    <LessonColumnContent
      followedCourse={followedCourse}
      day={day}
      isCurrent={isCurrent}
      lesson={lesson}
      isClassOff={courseDate?.isOff ?? false}
      isCopied={isCopied}
      isChangedSinceCopied={isCopied && changedSinceCopied(parentCardStack!, loadedCardStack!)}
      doCopyLesson={doCopyLesson}
    />
  )
})

const LessonColumnContent = tracked(function LessonColumnContent({
  followedCourse,
  day,
  isCurrent,
  lesson,
  isClassOff,
  isCopied,
  isChangedSinceCopied,
  doCopyLesson,
}: {
  followedCourse: Course
  day: string
  isCurrent: boolean
  lesson: FollowedLesson
  isClassOff: boolean
  isCopied: boolean
  isChangedSinceCopied: boolean
  doCopyLesson?: () => void
}) {
  return (
    <section
      className="flex flex-col gap-3"
      data-test="followed course lesson column"
      data-day={day}
    >
      <div
        className="mx-2.25 flex shrink-0 items-center justify-between rounded-full bg-white py-3 pr-3 pl-4.5 shadow-md"
        data-test="followed course day marker"
      >
        <div className="flex flex-col gap-0.5">
          <div
            className={
              isCurrent ? "text-[0.9375rem] text-link-blue" : "text-[0.9375rem] text-black/70"
            }
            data-test="followed course weekday"
          >
            {moment(day).format("dddd")}
          </div>
          <div
            className={
              isCurrent
                ? "text-lg leading-none font-black text-link-blue"
                : "text-lg leading-none font-black"
            }
            data-test={isCurrent ? "followed course current lesson" : undefined}
          >
            {moment(day).format("MMMM D")}
          </div>
        </div>
      </div>
      <div
        className="mx-2.25 overflow-hidden rounded-md bg-white shadow-md"
        data-test="followed course lesson card"
      >
        <div
          className="set-course-color-vars take-course-background-color flex min-h-9 items-center justify-between gap-2 border-b border-black/10 px-2.5 py-2 text-[0.9375rem] leading-none"
          data-color={followedCourse.attributes.color}
        >
          <span className="min-w-0 flex-1 font-bold [overflow-wrap:anywhere]">
            {followedCourse.attributes.title}
          </span>
          {isCopied ? (
            <span
              className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-black/60 shadow-sm"
              data-test="followed course menu already copied"
            >
              Copied
            </span>
          ) : (
            doCopyLesson !== undefined && (
              <Button
                data-test="followed course menu use lesson"
                onPress={doCopyLesson}
                className="group flex shrink-0 cursor-pointer items-center gap-1 rounded-full bg-white px-2 py-1 text-xs leading-none font-semibold text-black/60 shadow-sm outline-hidden hover:text-black/80 focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-focus-ring"
              >
                <IconAddCircleBold className="size-3.5 text-black/50 transition-colors group-hover:text-black/70" />
                Use this lesson
              </Button>
            )
          )}
        </div>
        {isChangedSinceCopied && (
          <div
            className="flex items-center justify-between gap-2 border-b border-black/10 bg-yellow-notification px-2.5 py-2 text-sm"
            data-test="followed course changed since copied"
          >
            <p>This lesson changed since you copied it.</p>
            {doCopyLesson !== undefined && (
              <Button
                data-test="followed course copy again"
                onPress={doCopyLesson}
                className="shrink-0 cursor-pointer rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-black/60 shadow-sm hover:text-black/80"
              >
                Copy again
              </Button>
            )}
          </div>
        )}
        <div className="py-2" data-test="followed course lesson body">
          {lesson.status === "loading" ? (
            <Loading />
          ) : lesson.status === "error" ? (
            <ColumnNotice dataTest="followed course lesson error">
              This lesson couldn't be loaded.
            </ColumnNotice>
          ) : lesson.status === "none" ? (
            <ColumnNotice dataTest="followed course no lesson">
              {isClassOff ? "No class" : "No lesson"}
            </ColumnNotice>
          ) : (
            // Keyed so a day that now holds a different lesson (say, after a bump) starts over.
            <LessonPreview key={lesson.lesson.id} lesson={lesson.lesson} />
          )}
        </div>
      </div>
    </section>
  )
})

function LessonPreview({ lesson }: { lesson: UnifiedCardStack }) {
  return (
    <div data-test="followed course lesson" data-title={lesson.attributes.title ?? ""}>
      <UnifiedLessonSimpleReadOnly lesson={lesson} hideButtons />
    </div>
  )
}

function ColumnNotice({ dataTest, children }: { dataTest: string; children: string }) {
  return (
    <div className="px-3 py-6 text-center text-[0.9375rem] text-gray-500" data-test={dataTest}>
      {children}
    </div>
  )
}

function Loading() {
  return (
    <div
      role="status"
      aria-label="Loading lesson"
      className="flex justify-center py-6"
      data-test="followed course lesson loading"
    >
      <ThreeDotSpinner />
    </div>
  )
}
